import clsx from "clsx";
import { useState } from "react";

type AvatarSize = "sm" | "md" | "lg";

interface AvatarProps {
	name: string;
	src?: string;
	size?: AvatarSize;
	bgColor?: string;
	textColor?: string;
	className?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
	sm: "h-24 w-24 text-xs",
	md: "h-32 w-32 text-sm",
	lg: "h-40 w-40 text-base",
};

const getInitials = (name: string) =>
	name
		.split(" ")
		.map((part) => part[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();

const Avatar = ({
	name,
	src,
	size = "md",
	bgColor = "bg-primary-200",
	textColor = "text-primary-800",
	className,
}: AvatarProps) => {
	const [imgStatus, setImgStatus] = useState<"loading" | "loaded" | "error">(
		src ? "loading" : "error",
	);

	const showImage = src && imgStatus !== "error";

	return (
		<div
			className={clsx(
				"relative inline-flex items-center justify-center rounded-full font-medium select-none shrink-0 transition-transform duration-200 hover:scale-110",
				bgColor,
				textColor,
				sizeClasses[size],
				className,
			)}
			title={name}
		>
			{showImage ? (
				<>
					{imgStatus === "loading" && (
						<div className="absolute inset-0 rounded-full bg-primary-100 animate-pulse" />
					)}
					<img
						src={src}
						alt={name}
						onLoad={() => setImgStatus("loaded")}
						onError={() => setImgStatus("error")}
						className={clsx(
							"h-full w-full rounded-full object-cover transition-opacity duration-300",
							imgStatus === "loaded" ? "opacity-100" : "opacity-0",
						)}
					/>
				</>
			) : (
				getInitials(name)
			)}
		</div>
	);
};

export default Avatar;
